if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer && this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        this.bindThumbnailEvents();
        if (this.elements.thumbnails && this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }

        this.initializeYKVariantMedia();
      }

      disconnectedCallback() {
        this.ykVariantChangeUnsubscriber?.();
      }

      bindThumbnailEvents() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          if (mediaToSwitch.dataset.mediaGalleryBound === 'true') return;
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          mediaToSwitch.dataset.mediaGalleryBound = 'true';
        });
      }

      initializeYKVariantMedia() {
        const productRoot = this.closest('.product');
        this.ykProductInfo =
          productRoot?.querySelector('product-info') ||
          this.closest('section')?.querySelector('product-info');

        if (!this.ykProductInfo) {
          const sectionId = String(this.id || '').replace(/^MediaGallery-/, '');
          if (sectionId) {
            this.ykProductInfo = document.querySelector(
              `product-info[data-section="${sectionId}"], product-info[data-original-section="${sectionId}"]`
            );
          }
        }

        const productUrl = this.ykProductInfo?.dataset?.url;
        if (!productUrl) return;

        fetch(`${productUrl}.js`)
          .then((response) => {
            if (!response.ok) throw new Error(`Product media request failed: ${response.status}`);
            return response.json();
          })
          .then((product) => {
            this.ykProductData = product;
            const options = Array.isArray(product.options) ? product.options : [];
            this.ykColorIndex = options.findIndex((option) => {
              const name = typeof option === 'string' ? option : option?.name;
              return String(name || '').toLowerCase() === 'color';
            });
            if (this.ykColorIndex < 0) return;

            const colors = new Set(
              (product.variants || [])
                .map((variant) => this.getYKVariantOptions(variant)[this.ykColorIndex])
                .filter(Boolean)
            );
            const imageMedia = this.getYKImageMedia(product);
            this.ykPairMode = colors.size > 1 && imageMedia.length === colors.size * 2;
            this.ykKickbackMode = String(product.id) === '8388887445561';
            if (!this.ykPairMode && !this.ykKickbackMode) return;

            const currentVariantId = this.ykProductInfo?.querySelector('input[name="id"]')?.value;
            if (currentVariantId) this.applyYKVariantMedia(currentVariantId);
          })
          .catch((error) => console.warn('YK gallery enhancement skipped.', error));

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          const sectionId = this.ykProductInfo?.dataset?.originalSection || this.ykProductInfo?.dataset?.section;
          this.ykVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId && sectionId && data.sectionId !== sectionId) return;
            if (!data?.variant?.id) return;
            window.setTimeout(() => this.applyYKVariantMedia(data.variant.id), 80);
          });
        }
      }

      getYKVariantOptions(variant) {
        if (Array.isArray(variant?.options)) return variant.options;
        return [variant?.option1, variant?.option2, variant?.option3];
      }

      getYKImageMedia(product) {
        if (!Array.isArray(product?.media)) return [];
        return product.media.filter((media) => !media?.media_type || media.media_type === 'image');
      }

      normalizeYKImageUrl(source) {
        if (source && typeof source === 'object') {
          source = source.src || source.url || source.originalSrc || source.preview_image?.src || '';
        }
        if (!source) return '';
        try {
          const url = new URL(source, window.location.origin);
          return url.pathname.replace(/_\d+x\d*(?=\.[a-z0-9]+$)/i, '');
        } catch {
          return String(source).split('?')[0];
        }
      }

      getYKMediaSource(media) {
        return media?.preview_image?.src || media?.src || media?.image?.src || '';
      }

      getYKMediaNumericId(media) {
        if (media?.id == null) return '';
        return String(media.id).split('/').pop();
      }

      getYKDomMediaNumericId(item) {
        const raw = String(item?.dataset?.mediaId || '');
        const match = raw.match(/(\d+)$/);
        return match ? match[1] : '';
      }

      resetYKGalleryVisibility() {
        const items = Array.from(this.elements.viewer?.querySelectorAll('li[data-media-id]') || []);
        items.forEach((item) => item.hidden = false);
        if (this.elements.thumbnails) {
          Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).forEach((item) => item.hidden = false);
        }
      }

      applyYKVariantMedia(variantId) {
        if (!this.ykProductData || (!this.ykPairMode && !this.ykKickbackMode)) return;
        const variant = (this.ykProductData.variants || []).find((item) => String(item.id) === String(variantId));
        if (!variant) return;

        let allowedIds = new Set();
        const imageMedia = this.getYKImageMedia(this.ykProductData);

        if (this.ykKickbackMode) {
          const color = String(this.getYKVariantOptions(variant)[this.ykColorIndex] || '').toLowerCase();
          const marker = color.includes('gray') ? 'ybs' : color.includes('black') ? 'ybt' : '';
          if (!marker) return;
          imageMedia.forEach((media) => {
            const alt = String(media?.alt || '').toLowerCase();
            if (alt.includes(marker) || alt.includes('size-')) {
              const id = this.getYKMediaNumericId(media);
              if (id) allowedIds.add(id);
            }
          });
        } else {
          const featuredUrl = this.normalizeYKImageUrl(variant.featured_image || variant.featured_media?.preview_image);
          let featuredIndex = imageMedia.findIndex(
            (media) => this.normalizeYKImageUrl(this.getYKMediaSource(media)) === featuredUrl
          );

          if (featuredIndex < 0 && variant.featured_media?.id != null) {
            const featuredId = String(variant.featured_media.id);
            featuredIndex = imageMedia.findIndex((media) => String(media?.id) === featuredId);
          }

          if (featuredIndex < 0) {
            this.resetYKGalleryVisibility();
            return;
          }

          const pairStart = Math.floor(featuredIndex / 2) * 2;
          imageMedia.slice(pairStart, pairStart + 2).forEach((media) => {
            const id = this.getYKMediaNumericId(media);
            if (id) allowedIds.add(id);
          });
        }

        if (!allowedIds.size) {
          this.resetYKGalleryVisibility();
          return;
        }

        const allItems = Array.from(this.elements.viewer?.querySelectorAll('li[data-media-id]') || []);
        const matchingItems = allItems.filter((item) => allowedIds.has(this.getYKDomMediaNumericId(item)));
        if (!matchingItems.length) {
          this.resetYKGalleryVisibility();
          return;
        }

        this.filterYKGalleryByIds(allowedIds);
        this.filterYKModalByIds(allowedIds);
      }

      filterYKGalleryByIds(allowedIds) {
        const mediaItems = Array.from(this.elements.viewer?.querySelectorAll('li[data-media-id]') || []);
        mediaItems.forEach((item) => {
          item.hidden = !allowedIds.has(this.getYKDomMediaNumericId(item));
        });

        const visibleItems = mediaItems.filter((item) => !item.hidden);
        const visibleTargets = new Set(visibleItems.map((item) => item.dataset.mediaId));

        if (this.elements.thumbnails) {
          Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).forEach((thumbnail) => {
            thumbnail.hidden = !visibleTargets.has(thumbnail.dataset.target);
          });
          this.bindThumbnailEvents();
        }

        const total = this.elements.viewer?.querySelector('.slider-counter--total');
        const current = this.elements.viewer?.querySelector('.slider-counter--current');
        if (total) total.textContent = String(visibleItems.length);
        if (current) current.textContent = '1';

        const active = mediaItems.find((item) => item.classList.contains('is-active') && !item.hidden);
        if (!active && visibleItems[0]) this.setActiveMedia(visibleItems[0].dataset.mediaId, false);

        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      filterYKModalByIds(allowedIds) {
        const sectionId = this.ykProductInfo?.dataset?.originalSection || this.ykProductInfo?.dataset?.section;
        const modal = sectionId ? document.querySelector(`#ProductModal-${sectionId}`) : null;
        const content = modal?.querySelector('.product-media-modal__content');
        if (!content) return;

        Array.from(content.children).forEach((item) => {
          const raw = String(item?.dataset?.mediaId || item?.id || '');
          const match = raw.match(/(\d+)$/);
          if (!match) return;
          item.hidden = !allowedIds.has(match[1]);
        });
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer?.querySelector('[data-media-id]:not([hidden])') ||
          this.elements.viewer?.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }
          this.elements.viewer?.resetPages?.();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail || activeThumbnail.hidden) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        const button = thumbnail.querySelector('button');
        if (!button) return;
        button.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;
        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', true), 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems?.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
